import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usuarioService } from '../services';
import useDebounce from '../hooks/useDebounce';
import { 
  Users, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Shield,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  UserCheck,
  Filter
} from 'lucide-react';

const GestaoUsuarios = () => {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Aplicar debounce na pesquisa com 500ms de delay
  const debouncedSearch = useDebounce(search, 500);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    confirmarSenha: '',
    senhaLiberacao: '',
    confirmarSenhaLiberacao: '',
    cargo: 'balconista'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLiberacaoPassword, setShowLiberacaoPassword] = useState(false);
  const [showConfirmLiberacaoPassword, setShowConfirmLiberacaoPassword] = useState(false);

  const tiposUsuario = [
    { value: 'admin', label: 'Administrador', description: 'Acesso total ao sistema' },
    { value: 'farmaceutico', label: 'Farmacêutico', description: 'Acesso total exceto usuários admin' },
    { value: 'balconista', label: 'Balconista', description: 'Acesso limitado, liberações precisam de autorização' },
    { value: 'visitante', label: 'Visitante', description: 'Apenas visualização de medicamentos' }
  ];

  const carregarUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: debouncedSearch,
        tipo: filtroTipo
      };
      
      const response = await usuarioService.getAll(params);
      setUsuarios(response.usuarios || []);
      setTotalPages(response.totalPages || 1);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar usuários' });
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch, filtroTipo]);

  useEffect(() => {
    carregarUsuarios();
  }, [carregarUsuarios]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    if (e.target.value !== debouncedSearch) {
      setIsSearching(true);
    }
  };

  // Resetar página quando o debouncedSearch muda
  useEffect(() => {
    setCurrentPage(1);
    setIsSearching(false);
  }, [debouncedSearch]);

  const handleTipoChange = (e) => {
    setFiltroTipo(e.target.value);
    setCurrentPage(1);
  };

  const openModal = (usuario = null) => {
    if (usuario) {
      setEditingUser(usuario.id);
      setFormData({
        nome: usuario.nome,
        email: usuario.email,
        senha: '',
        confirmarSenha: '',
        senhaLiberacao: '',
        confirmarSenhaLiberacao: '',
        cargo: usuario.cargo
      });
    } else {
      setEditingUser(null);
      setFormData({
        nome: '',
        email: '',
        senha: '',
        confirmarSenha: '',
        senhaLiberacao: '',
        confirmarSenhaLiberacao: '',
        cargo: 'balconista'
      });
    }
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      nome: '',
      email: '',
      senha: '',
      confirmarSenha: '',
      senhaLiberacao: '',
      confirmarSenhaLiberacao: '',
      cargo: 'balconista'
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowLiberacaoPassword(false);
    setShowConfirmLiberacaoPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validações
    if (!editingUser && formData.senha !== formData.confirmarSenha) {
      setMessage({ type: 'error', text: 'As senhas não coincidem' });
      return;
    }

    if (!editingUser && formData.senha.length < 6) {
      setMessage({ type: 'error', text: 'A senha deve ter pelo menos 6 caracteres' });
      return;
    }

    if (editingUser && formData.senha && formData.senha !== formData.confirmarSenha) {
      setMessage({ type: 'error', text: 'As senhas não coincidem' });
      return;
    }

    if (editingUser && formData.senha && formData.senha.length < 6) {
      setMessage({ type: 'error', text: 'A senha deve ter pelo menos 6 caracteres' });
      return;
    }

    // Validar senha de liberação para balconistas e farmacêuticos
    if (formData.cargo === 'balconista' || formData.cargo === 'farmaceutico') {
      if (!editingUser && !formData.senhaLiberacao && formData.cargo === 'balconista') {
        setMessage({ type: 'error', text: 'Senha de liberação é obrigatória para balconistas' });
        return;
      }
      
      if (formData.senhaLiberacao && formData.senhaLiberacao !== formData.confirmarSenhaLiberacao) {
        setMessage({ type: 'error', text: 'As senhas de liberação não coincidem' });
        return;
      }
      
      if (formData.senhaLiberacao && formData.senhaLiberacao.length < 6) {
        setMessage({ type: 'error', text: 'A senha de liberação deve ter pelo menos 6 caracteres' });
        return;
      }
    }

    // Verificar permissões para criar admin
    if (formData.cargo === 'admin' && user.cargo !== 'admin') {
      setMessage({ type: 'error', text: 'Apenas administradores podem criar usuários administradores' });
      return;
    }

    try {
      setLoading(true);
      
      const userData = {
        nome: formData.nome,
        email: formData.email,
        cargo: formData.cargo
      };

      if (formData.senha) {
        userData.senha = formData.senha;
      }

      if (formData.senhaLiberacao && (formData.cargo === 'balconista' || formData.cargo === 'farmaceutico')) {
        userData.senhaLiberacao = formData.senhaLiberacao;
      }

      if (editingUser) {
        await usuarioService.update(editingUser, userData);
        setMessage({ type: 'success', text: 'Usuário atualizado com sucesso!' });
      } else {
        await usuarioService.create(userData);
        setMessage({ type: 'success', text: 'Usuário criado com sucesso!' });
      }

      closeModal();
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Erro ao salvar usuário' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, nome) => {
    if (window.confirm(`Tem certeza que deseja excluir o usuário "${nome}"?`)) {
      try {
        await usuarioService.delete(id);
        setMessage({ type: 'success', text: 'Usuário excluído com sucesso' });
        carregarUsuarios();
      } catch (error) {
        console.error('Erro ao excluir usuário:', error);
        setMessage({ 
          type: 'error', 
          text: error.response?.data?.message || 'Erro ao excluir usuário' 
        });
      }
    }
  };

  const toggleUserStatus = async (id, ativo) => {
    try {
      await usuarioService.toggleStatus(id, !ativo);
      setMessage({ 
        type: 'success', 
        text: `Usuário ${!ativo ? 'ativado' : 'desativado'} com sucesso` 
      });
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      setMessage({ type: 'error', text: 'Erro ao alterar status do usuário' });
    }
  };

  const getCargoColor = (cargo) => {
    const cores = {
      'admin': '#dc3545',
      'farmaceutico': '#007bff',
      'balconista': '#28a745',
      'visitante': '#6f42c1'
    };
    return cores[cargo] || '#6c757d';
  };

  const getCargoLabel = (cargo) => {
    const labels = {
      'admin': 'Administrador',
      'farmaceutico': 'Farmacêutico',
      'balconista': 'Balconista',
      'visitante': 'Visitante'
    };
    return labels[cargo] || cargo;
  };

  // Verificar permissões - apenas admin
  if (user.cargo !== 'admin') {
    return (
      <div className="container">
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Shield size={48} style={{ color: '#dc3545', marginBottom: '20px' }} />
            <h3>Acesso Negado</h3>
            <p>Apenas administradores têm permissão para acessar a gestão de usuários.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !showModal) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ color: '#333', margin: '0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Users size={32} />
            Gestão de Usuários
          </h1>
          <p style={{ color: '#667', margin: '5px 0 0 0' }}>
            Gerencie usuários e permissões do sistema
          </p>
        </div>
        
        <button onClick={() => openModal()} className="btn btn-primary">
          <Plus size={20} />
          Novo Usuário
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '20px' }}>
          {message.type === 'error' && <AlertTriangle size={20} />}
          {message.type === 'success' && <CheckCircle size={20} />}
          {message.text}
        </div>
      )}

      <div className="card">
        {/* Filtros */}
        <div className="search-box">
          <div className="search-input" style={{ position: 'relative' }}>
            <Search size={20} style={{ 
              position: 'absolute', 
              left: '10px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: isSearching ? '#007bff' : '#667'
            }} />
            {isSearching && (
              <div style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#007bff',
                fontSize: '12px'
              }}>
                Buscando...
              </div>
            )}
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nome ou email..."
              value={search}
              onChange={handleSearch}
              style={{ 
                paddingLeft: '40px',
                paddingRight: isSearching ? '80px' : '10px'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Filter size={20} color="#667" />
            <select
              className="form-control"
              value={filtroTipo}
              onChange={handleTipoChange}
              style={{ minWidth: '150px' }}
            >
              <option value="">Todos os tipos</option>
              {tiposUsuario.map(tipo => (
                <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabela */}
        {usuarios.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Tipo</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((usuario) => (
                    <tr key={usuario.id}>
                      <td>
                        <div style={{ fontWeight: '500' }}>{usuario.nome}</div>
                      </td>
                      <td>{usuario.email}</td>
                      <td>
                        <span style={{
                          background: getCargoColor(usuario.cargo),
                          color: 'white',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {getCargoLabel(usuario.cargo)}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          color: usuario.ativo ? '#28a745' : '#dc3545',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}>
                          {usuario.ativo ? (
                            <>
                              <CheckCircle size={16} />
                              Ativo
                            </>
                          ) : (
                            <>
                              <AlertTriangle size={16} />
                              Inativo
                            </>
                          )}
                        </span>
                      </td>
                      <td>
                        {new Date(usuario.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => openModal(usuario)}
                            className="btn btn-secondary"
                            style={{ padding: '6px 10px' }}
                          >
                            <Edit size={16} />
                          </button>
                          
                          {user.cargo === 'admin' && (
                            <>
                              <button
                                onClick={() => toggleUserStatus(usuario.id, usuario.ativo)}
                                className={`btn ${usuario.ativo ? 'btn-danger' : 'btn-success'}`}
                                style={{ padding: '6px 10px' }}
                              >
                                <UserCheck size={16} />
                              </button>
                              
                              <button
                                onClick={() => handleDelete(usuario.id, usuario.nome)}
                                className="btn btn-danger"
                                style={{ padding: '6px 10px' }}
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                
                <span style={{ margin: '0 20px', color: '#667' }}>
                  Página {currentPage} de {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px',
            color: '#667'
          }}>
            <Users size={48} style={{ opacity: 0.5, marginBottom: '15px' }} />
            <h3>Nenhum usuário encontrado</h3>
            <p>
              {search || filtroTipo 
                ? 'Tente ajustar os filtros de busca' 
                : 'Comece cadastrando o primeiro usuário'
              }
            </p>
            {!search && !filtroTipo && (
              <button onClick={() => openModal()} className="btn btn-primary" style={{ marginTop: '15px' }}>
                <Plus size={20} />
                Cadastrar Primeiro Usuário
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ 
            width: '100%', 
            maxWidth: '500px', 
            margin: '20px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0 }}>
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </h3>
              <button
                onClick={closeModal}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#667'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Nome Completo</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de Usuário</label>
                <select
                  className="form-control"
                  value={formData.cargo}
                  onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                  required
                >
                  {tiposUsuario.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label} - {tipo.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {editingUser ? 'Nova Senha (deixe em branco para não alterar)' : 'Senha'}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    value={formData.senha}
                    onChange={(e) => setFormData({...formData, senha: e.target.value})}
                    required={!editingUser}
                    minLength={6}
                    style={{ paddingRight: '45px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      color: '#667'
                    }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Confirmar Senha</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="form-control"
                    value={formData.confirmarSenha}
                    onChange={(e) => setFormData({...formData, confirmarSenha: e.target.value})}
                    required={!editingUser || formData.senha}
                    style={{ paddingRight: '45px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      color: '#667'
                    }}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Campos de senha de liberação para balconistas e farmacêuticos */}
              {(formData.cargo === 'balconista' || formData.cargo === 'farmaceutico') && (
                <>
                    <div className="form-group">
                    <label className="form-label">
                      {editingUser ? 'Nova Senha de Liberação (deixe em branco para não alterar)' : 'Senha de Liberação'}
                      {formData.cargo === 'farmaceutico' && <span style={{ color: '#667', fontSize: '12px', fontWeight: 'normal' }}> (opcional)</span>}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showLiberacaoPassword ? 'text' : 'password'}
                        className="form-control"
                        value={formData.senhaLiberacao}
                        onChange={(e) => setFormData({...formData, senhaLiberacao: e.target.value})}
                        required={!editingUser && formData.cargo === 'balconista'}
                        minLength={6}
                        style={{ paddingRight: '45px' }}
                        placeholder={formData.cargo === 'balconista' ? "Senha específica para liberações" : "Senha para liberações (opcional)"}
                      />
                      <button
                        type="button"
                        onClick={() => setShowLiberacaoPassword(!showLiberacaoPassword)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          color: '#667'
                        }}
                      >
                        {showLiberacaoPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Confirmar Senha de Liberação</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showConfirmLiberacaoPassword ? 'text' : 'password'}
                        className="form-control"
                        value={formData.confirmarSenhaLiberacao}
                        onChange={(e) => setFormData({...formData, confirmarSenhaLiberacao: e.target.value})}
                        required={(!editingUser && formData.cargo === 'balconista') || formData.senhaLiberacao}
                        style={{ paddingRight: '45px' }}
                        placeholder="Confirme a senha de liberação"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmLiberacaoPassword(!showConfirmLiberacaoPassword)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          color: '#667'
                        }}
                      >
                        {showConfirmLiberacaoPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : (editingUser ? 'Atualizar' : 'Criar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestaoUsuarios;